// Shared cost math for purchase requests. The order-level `discount` (an absolute
// amount in the order's `currency`) is applied off the net subtotal, before VAT,
// to the matching currency bucket only. `vat == subtotal * rate` holds for both
// VAT-included and VAT-excluded pricing, so VAT is simply recomputed on the net.

export function computeLine(price, qty, vatIncluded, international, taxRate) {
  if (price == null || isNaN(price)) return null
  const rate = international ? 0 : Number(taxRate ?? 0.19)
  const line = Number(price) * Number(qty || 0)
  if (rate === 0) return { subtotal: line, vat: 0, total: line, rate: 0 }
  if (vatIncluded) {
    const total = line
    const subtotal = total / (1 + rate)
    return { subtotal, vat: total - subtotal, total, rate }
  }
  return { subtotal: line, vat: line * rate, total: line * (1 + rate), rate }
}

// Apply the order discount to a bucket {subtotal, vat, total} when `cur` matches
// the order's discount currency. Keeps the original subtotal for display and
// recomputes vat/total from the discounted net. Always returns a `discount` field.
function applyDiscount(bucket, cur, discount, discCurrency, rate) {
  if (!(discount > 0) || cur !== discCurrency) return { ...bucket, discount: 0 }
  const applied = Math.min(discount, bucket.subtotal)
  const net = bucket.subtotal - applied
  const vat = net * rate
  return { subtotal: bucket.subtotal, discount: applied, vat, total: net + vat }
}

export function lineTotals(p) {
  const baseRate = p.international ? 0 : Number(p.tax_rate ?? 0.19)
  const discount = Math.max(0, Number(p.discount) || 0)
  const discCurrency = p.currency || 'CLP'

  if (p.is_multi && Array.isArray(p.items) && p.items.length > 0) {
    const raw = {}
    for (const it of p.items) {
      const unitPrice = it.final_unit_price != null ? it.final_unit_price : it.estimated_unit_price
      const t = computeLine(unitPrice, it.quantity, p.vat_included, p.international, p.tax_rate)
      if (!t) continue
      const cur = it.currency || 'CLP'
      if (!raw[cur]) raw[cur] = { subtotal: 0, vat: 0, total: 0 }
      raw[cur].subtotal += t.subtotal
      raw[cur].vat += t.vat
      raw[cur].total += t.total
    }
    const byCurr = {}
    for (const [cur, b] of Object.entries(raw)) {
      byCurr[cur] = applyDiscount(b, cur, discount, discCurrency, baseRate)
    }
    const currencies = Object.keys(byCurr)
    if (currencies.length === 0) return { subtotal: null, vat: null, total: null, rate: baseRate, multi: true, byCurr }
    if (currencies.length === 1) {
      const c = currencies[0]
      return { ...byCurr[c], rate: baseRate, currency: c, multi: true, byCurr }
    }
    return { subtotal: null, vat: null, total: null, rate: baseRate, multi: true, mixed: true, byCurr }
  }

  const unitPrice = p.final_unit_price != null ? p.final_unit_price : p.estimated_unit_price
  const t = computeLine(unitPrice, p.quantity, p.vat_included, p.international, p.tax_rate)
  if (!t) return { subtotal: null, vat: null, total: null, rate: baseRate }
  const a = applyDiscount({ subtotal: t.subtotal, vat: t.vat, total: t.total }, p.currency || 'CLP', discount, discCurrency, t.rate)
  return { ...a, rate: t.rate }
}
